import * as React from 'react';
import { cn } from './utils';

/**
 * RadioCard — fat radio cell rendered as a labeled card.
 *
 * The visible affordance is the parent `<label>`; the native radio is
 * `sr-only` so screen readers + keyboard navigation work via the platform
 * radio semantics. Use inside a `<div role="radiogroup">` (the radio
 * group is implicit when all RadioCards share the same `name`).
 */
export interface RadioCardProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  description?: string;
}

export const RadioCard = React.forwardRef<HTMLInputElement, RadioCardProps>(
  ({ label, description, checked, className, ...props }, ref) => (
    <label
      className={cn(
        'flex flex-1 cursor-pointer flex-col gap-1 rounded-md p-4 text-left transition-colors',
        // Match the rest of the form's soft-cell grammar: bg-foreground/5
        // at rest, /10 on hover/checked. A thin inset ring at full
        // foreground signals selection without the heavy black border
        // that breaks the visual rhythm with adjacent cells.
        checked
          ? 'bg-foreground/10 ring-1 ring-inset ring-foreground'
          : 'bg-foreground/5 hover:bg-foreground/10',
        className,
      )}
    >
      <input ref={ref} type="radio" checked={checked} className="sr-only" {...props} />
      <span className="text-sm font-semibold text-foreground">{label}</span>
      {description ? (
        <span className="text-xs text-muted-foreground">{description}</span>
      ) : null}
    </label>
  ),
);
RadioCard.displayName = 'RadioCard';
